<template>
  <div v-cloak :class="['font-sans antialiased bg-gray-50 dark:bg-gray-900 flex flex-col', appShellClass]">
    <!-- Slide-out Documentation Trigger (top-right corner); server still enforces /docs auth -->
    <div v-if="auth.isAuthenticated" class="docs-trigger-zone">
      <a :href="docsUrl" target="_blank" rel="noopener noreferrer" class="docs-slide-button">
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253">
          </path>
        </svg>
        Documentation
      </a>
    </div>

    <!-- Government utility bar (same green bar as login; hidden on full-page auth routes) -->
    <GovUtilityBar
      v-if="!isFullPagePublicRoute"
      fixed
      @utility-action="onAppUtilityAction"
    />

    <!-- Main Layout with Sidebar -->
    <div
      v-if="!isFullPagePublicRoute"
      class="flex flex-1 min-h-0 pt-14 overflow-hidden"
    >
      <!-- Desktop Sidebar -->
      <div class="hidden lg:flex lg:flex-shrink-0 h-full min-h-0">
        <Sidebar />
      </div>

      <!-- Mobile Sidebar Overlay -->
      <div v-if="sidebarOpen" class="fixed inset-x-0 bottom-0 top-14 z-50 flex lg:hidden">
        <div class="fixed inset-x-0 bottom-0 top-14 bg-gray-600 bg-opacity-75" @click="closeSidebar"></div>
        <div class="relative flex flex-col w-72 max-w-[85vw] shrink-0 bg-white dark:bg-gray-800 h-full shadow-xl">
          <button type="button"
            class="absolute right-3 top-3 z-10 inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Close sidebar" @click="closeSidebar">
            <svg class="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <Sidebar />
        </div>
      </div>

      <!-- Main Content Area (min-w-0 so nested overflow-x-auto can scroll instead of widening the layout) -->
      <div class="flex flex-1 flex-col min-w-0 min-h-0">
        <!-- Mobile Toggle Button (Fixed Position) -->
        <div v-if="!sidebarOpen" class="fixed left-4 z-40 lg:hidden top-[4.75rem] sm:top-16">
          <SidebarToggle :is-open="false" @toggle="toggleSidebar" />
        </div>

        <!-- Main Content -->
        <main
          class="flex-1 min-w-0 bg-gray-50 dark:bg-gray-900 overflow-y-auto overflow-x-hidden min-h-0"
        >
          <div class="pt-4 sm:pt-6 pb-3 sm:pb-4 px-3 sm:px-5 lg:px-7 min-w-0 max-w-full">
            <!-- Mobile spacing for toggle button -->
            <div class="lg:hidden h-14 sm:h-12"></div>
            <router-view />
          </div>
        </main>
      </div>
    </div>

    <!-- Login / OTP / password reset: full container flow with internal scroll -->
    <div v-else class="flex-1 min-h-0 overflow-y-auto">
      <router-view />
    </div>

    <TeacherDetailsModal />
    <!-- Token Expiration Modal -->
    <TokenExpirationModal />

    <!-- Toast Notifications -->
    <ToastContainer />
  </div>
</template>

<style>
/* Prevent initial unstyled flicker before Vue mounts */
[v-cloak] {
  display: none;
}

/* Global font scaling using CSS custom property */
:root {
  --font-scale: 1;
}

/* Apply font scaling to main content and navigation only (not gov utility bar) */
main,
nav {
  font-size: calc(1rem * var(--font-scale, 1));
}

/* Slide-out Documentation Trigger Styles (below gov header on small screens to avoid overlap) */
.docs-trigger-zone {
  position: fixed;
  top: 0;
  right: 0;
  width: 60px;
  height: 60px;
  z-index: 9998;
  overflow: visible;
}

@media (max-width: 639px) {
  .docs-trigger-zone {
    top: 52px;
    height: 48px;
  }
}

.docs-slide-button {
  position: absolute;
  top: 12px;
  right: -180px;
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px 0 0 8px;
  box-shadow: -2px 2px 10px rgba(0, 0, 0, 0.2);
  text-decoration: none;
  white-space: nowrap;
  transition: right 0.3s ease-out, background 0.2s ease;
}

.docs-trigger-zone:hover .docs-slide-button {
  right: 0;
}

.docs-slide-button:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

/* Dark mode styles for docs button */
.dark .docs-slide-button {
  background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
}

.dark .docs-slide-button:hover {
  background: linear-gradient(135deg, #4338ca 0%, #3730a3 100%);
}
</style>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from './stores/auth'
import { themeStore } from './stores/theme'
import GovUtilityBar, { type GovUtilityAction } from './components/GovUtilityBar.vue'
import Sidebar from './components/Sidebar.vue'
import SidebarToggle from './components/SidebarToggle.vue'
import TeacherDetailsModal from './components/TeacherDetailsModal.vue'
import TokenExpirationModal from './components/TokenExpirationModal.vue'
import ToastContainer from './components/ToastContainer.vue'
import { useRoleGuard } from './composables/useRoleGuard'
import { tokenExpirationService } from './services/tokenExpirationService'
import { isTeacherSession } from './utils/isTeacherSession'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const currentUser = computed(() => auth.currentUser)

// Role guard
const {
  showUserManagement,
  canManageSchools,
  canManageTeachers,
  canManageMedicalRecords,
  isTeacher
} = useRoleGuard()

// Super admin check
const isSuperAdmin = computed(() => {
  return currentUser.value?.role === 'super_admin'
})

// Sidebar state
const sidebarOpen = ref(false)

// Sidebar controls
const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

const closeSidebar = () => {
  sidebarOpen.value = false
}

onMounted(() => { })

const handleLogout = async () => {
  tokenExpirationService.stopTokenExpirationCheck()
  await auth.logout()
  sidebarOpen.value = false
  router.push('/login')
}

// Close sidebar when route changes (mobile)
const closeSidebarOnRouteChange = () => {
  sidebarOpen.value = false
}

// Documentation: Express serves /docs from dist/docs (prod) or documentation/build (dev); vite proxies `/docs` in dev.
// Override with VITE_DOCS_URL if docs are hosted elsewhere (must be full URL or absolute path).
const docsUrl = (() => {
  const raw = import.meta.env.VITE_DOCS_URL?.trim()
  if (raw) return raw.endsWith('/') ? raw : `${raw}/`
  return '/docs/'
})()

// Public routes that don't require authentication
const publicRoutes = ['/login', '/otp-login', '/forgot-password', '/reset-password']

/** Full-page auth views: let #app grow with content so the window is the only vertical scroll. */
const isFullPagePublicRoute = computed(() => publicRoutes.includes(route.path))

const appShellClass = computed(() =>
  isFullPagePublicRoute.value ? 'h-full w-full overflow-hidden' : 'h-full max-h-full w-full min-h-0 overflow-hidden'
)

// Function to check if route is public
const isPublicRoute = (path: string) => publicRoutes.includes(path)

function onAppUtilityAction(action: GovUtilityAction) {
  if (action === 'helpdesk' || action === 'transfer-policy') return
  const helpline = import.meta.env.VITE_LOGIN_HELPLINE?.trim()
  if (helpline) {
    window.alert(`Helpline: ${helpline}`)
  }
}

// Watch for route changes to close sidebar on mobile and handle auth redirects
watch(() => route.path, (newPath) => {
  closeSidebarOnRouteChange()

  // CRITICAL: Stop token expiration service when navigating to login pages
  if (isPublicRoute(newPath)) {
    tokenExpirationService.stopTokenExpirationCheck()
    return // Let router guard handle public routes
  }

  // Only redirect if not authenticated and not on a public route
  if (!auth.isAuthenticated && !isPublicRoute(newPath)) {
    tokenExpirationService.stopTokenExpirationCheck()
    router.push('/login')
  } else if (auth.isAuthenticated && isPublicRoute(newPath) && (newPath === '/login' || newPath === '/otp-login')) {
    // If authenticated user tries to access login pages, redirect to dashboard
    router.push('/')
  }
}, { immediate: false }) // Don't run immediately on mount

// CRITICAL: Stop token expiration when logged out; teachers (OTP) never run the warning poller
watch(
  () => ({ authed: auth.isAuthenticated, role: auth.currentUser?.role }),
  ({ authed, role }) => {
    if (!authed) {
      tokenExpirationService.stopTokenExpirationCheck()
    } else if (role && String(role).trim().toLowerCase() === 'teacher') {
      tokenExpirationService.stopTokenExpirationCheck()
    } else {
      tokenExpirationService.startTokenExpirationCheck()
    }
  },
  { immediate: true }
)

onMounted(() => {
  // Initialize theme
  themeStore.initializeTheme()

  // CRITICAL: Check actual browser URL, not route.path (which may not be initialized yet)
  const browserPath = typeof window !== 'undefined' ? window.location.pathname : route.path
  const actualPath = browserPath || route.path

  // IMPORTANT: Don't redirect on mount if on public routes - let router guard handle it
  // Check both browser URL and route path to handle initial load correctly
  if (isPublicRoute(actualPath) || isPublicRoute(route.path)) {
    // Don't redirect public routes - they're already handled by router guard
    return
  }

  // Check if user is authenticated for protected routes
  if (!auth.isAuthenticated && !isPublicRoute(actualPath) && !isPublicRoute(route.path)) {
    router.push('/login')
  } else if (auth.isAuthenticated) {
    if (isTeacherSession()) {
      tokenExpirationService.stopTokenExpirationCheck()
    } else {
      tokenExpirationService.startTokenExpirationCheck()
    }
  }
})
</script>
